import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, CalendarDays, CalendarX2, ClipboardList, History, Loader2, LogOut, MapPin, Megaphone, Plus, Save, Trash2, UserRound } from "lucide-react";
import { api, formatApiError, useAuth } from "@/auth";
import { GROUPS, SEDES } from "@/i18n";
import Avatar from "@/components/Avatar";

const STATUS_BTNS = [
  { id: "presente", label: "Presente", on: "bg-emerald-500 text-white border-emerald-500", off: "border-emerald-500/40 text-emerald-300" },
  { id: "falta", label: "Falta", on: "bg-accent text-white border-accent", off: "border-accent/40 text-accent" },
  { id: "retraso", label: "Retraso", on: "bg-amber-400 text-black border-amber-400", off: "border-amber-400/50 text-amber-300" },
];

const STATUS_ES = { presente: "Presente", falta: "Falta", retraso: "Retraso" };

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none transition-colors duration-300 focus:border-accent";
const labelCls = "mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400";

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmtDay = (date) => new Date(`${date}T12:00:00`).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });

const tid = (s) => s.replace(/[^a-z0-9]/gi, "-").toLowerCase();

export default function TeacherPanel() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("lista");
  const [groups, setGroups] = useState([]);
  const [group, setGroup] = useState("");
  const [date, setDate] = useState(today());
  const [roster, setRoster] = useState([]);
  const [marks, setMarks] = useState({});
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const [tEvents, setTEvents] = useState([]);
  const [tCancellations, setTCancellations] = useState([]);
  const [tNotices, setTNotices] = useState([]);
  const [evForm, setEvForm] = useState({ title: "", description: "", date: "", groups: [] });
  const [cxF, setCxF] = useState({ group_code: "", date: "", reason: "" });
  const [nf, setNf] = useState({ title: "", body: "", groups: [] });
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", guardian_name: "", phone: "" });

  const loadRoster = (g) => {
    api
      .get(`/teacher/students/${encodeURIComponent(g)}`)
      .then((r) => {
        setRoster(r.data);
        setMarks(Object.fromEntries(r.data.map((s) => [s.email, { status: "presente", note: "" }])));
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const addStudent = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/teacher/students/${encodeURIComponent(group)}`, addForm);
      toast.success("Alumna/o añadido a la lista");
      setAddForm({ name: "", guardian_name: "", phone: "" });
      setShowAdd(false);
      loadRoster(group);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const loadHistory = (g) => {
    api.get(`/attendance?group=${encodeURIComponent(g)}`).then((r) => setHistory(r.data)).catch(() => {});
  };

  const loadAvisos = () => {
    api.get("/admin/events").then((r) => setTEvents(r.data)).catch(() => {});
    api.get("/admin/cancellations").then((r) => setTCancellations(r.data)).catch(() => {});
    api.get("/admin/notices").then((r) => setTNotices(r.data)).catch(() => {});
  };

  useEffect(() => {
    api
      .get("/teacher/my-groups")
      .then((r) => {
        setGroups(r.data);
        setGroup((g) => g || r.data[0] || "");
      })
      .catch((e) => toast.error(formatApiError(e)));
    loadAvisos();
  }, []);

  useEffect(() => {
    if (!group) return;
    api
      .get(`/teacher/students/${encodeURIComponent(group)}`)
      .then((r) => {
        setRoster(r.data);
        setMarks(Object.fromEntries(r.data.map((s) => [s.email, { status: "presente", note: "" }])));
      })
      .catch((e) => toast.error(formatApiError(e)));
    loadHistory(group);
  }, [group]);

  useEffect(() => {
    if (!group || !date || !roster.length) return;
    api
      .get(`/attendance?group=${encodeURIComponent(group)}&start=${date}&end=${date}`)
      .then((r) => {
        const defaults = Object.fromEntries(roster.map((s) => [s.email, { status: "presente", note: "" }]));
        if (r.data.length) {
          for (const rec of r.data[0].records || []) {
            if (defaults[rec.email]) defaults[rec.email] = { status: rec.status, note: rec.note || "" };
          }
        }
        setMarks(defaults);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, date, roster]);

  const mark = (email, status) => setMarks((m) => ({ ...m, [email]: { ...m[email], status } }));
  const note = (email, value) => setMarks((m) => ({ ...m, [email]: { ...m[email], note: value } }));

  const counts = { presente: 0, falta: 0, retraso: 0 };
  roster.forEach((s) => {
    counts[marks[s.email]?.status || "presente"] += 1;
  });

  const save = async () => {
    if (!roster.length) return;
    setBusy(true);
    try {
      await api.post("/teacher/attendance", {
        group_code: group,
        date,
        records: roster.map((s) => ({ email: s.email, name: s.name, status: marks[s.email]?.status || "presente", note: marks[s.email]?.note || null })),
      });
      toast.success("Asistencia guardada");
      loadHistory(group);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleEvGroup = (g) =>
    setEvForm((f) => ({ ...f, groups: f.groups.includes(g) ? f.groups.filter((x) => x !== g) : [...f.groups, g] }));
  const toggleNfGroup = (g) =>
    setNf((f) => ({ ...f, groups: f.groups.includes(g) ? f.groups.filter((x) => x !== g) : [...f.groups, g] }));

  const createEvent = async (e) => {
    e.preventDefault();
    if (!evForm.groups.length) {
      toast.error("Selecciona al menos una clase");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/events", { ...evForm, date: new Date(evForm.date).toISOString() });
      toast.success("Evento publicado: avisaremos por email a la clase");
      setEvForm({ title: "", description: "", date: "", groups: [] });
      loadAvisos();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const createCancellation = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/cancellations", { ...cxF, date: new Date(cxF.date).toISOString() });
      toast.success("Cancelación publicada: avisaremos por email a la clase");
      setCxF({ group_code: "", date: "", reason: "" });
      loadAvisos();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const createNotice = async (e) => {
    e.preventDefault();
    if (!nf.groups.length) {
      toast.error("Selecciona al menos una clase");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/notices", nf);
      toast.success("Comunicado publicado: avisaremos por email a la clase");
      setNf({ title: "", body: "", groups: [] });
      loadAvisos();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const delEvent = async (id) => {
    await api.delete(`/admin/events/${id}`).catch((e) => toast.error(formatApiError(e)));
    loadAvisos();
  };
  const delCx = async (id) => {
    await api.delete(`/admin/cancellations/${id}`).catch((e) => toast.error(formatApiError(e)));
    loadAvisos();
  };
  const delNotice = async (id) => {
    await api.delete(`/admin/notices/${id}`).catch((e) => toast.error(formatApiError(e)));
    loadAvisos();
  };

  const groupMeta = GROUPS.find((g) => g.code === group);
  const sede = groupMeta ? SEDES[groupMeta.sede] : null;

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
    navigate("/");
  };

  const fmtEv = (iso) =>
    new Date(iso).toLocaleDateString("es-ES", { timeZone: "Atlantic/Canary", weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

  const tabs = [
    { id: "lista", label: "Pasar lista", icon: ClipboardList, test: "teacher-tab-lista" },
    { id: "avisos", label: "Avisos", icon: Megaphone, test: "teacher-tab-avisos" },
  ];

  return (
    <div className="mx-auto max-w-5xl px-5 pb-24 pt-28 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-5">
          <Avatar testPrefix="teacher" />
          <div>
            <p className="mb-2 flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.3em] text-accent">
              <UserRound size={14} /> Panel de profesoras
            </p>
            <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">Hola, {user?.name}</h1>
          </div>
        </div>
        <button
          data-testid="teacher-logout"
          onClick={logout}
          className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-zinc-300 transition-all duration-300 hover:border-accent hover:text-accent"
        >
          <LogOut size={14} /> Salir
        </button>
      </div>

      <div className="mt-6 flex gap-2">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            data-testid={tb.test}
            onClick={() => setTab(tb.id)}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] transition-all duration-300 ${
              tab === tb.id ? "bg-accent text-white" : "border border-white/15 text-zinc-400 hover:text-white"
            }`}
          >
            <tb.icon size={14} />
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "lista" && (
        <>
          <div className="mt-8 flex flex-col gap-4 rounded-xl border border-white/10 bg-card p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div className="flex flex-wrap gap-2" data-testid="teacher-group-picker">
              {groups.map((g) => (
                <button
                  key={g}
                  data-testid={`teacher-group-${tid(g)}`}
                  onClick={() => setGroup(g)}
                  className={`rounded-full px-4 py-2 font-display text-xs font-extrabold uppercase tracking-wide transition-all duration-300 ${
                    group === g ? "bg-accent text-white" : "border border-white/15 text-zinc-400 hover:text-white"
                  }`}
                >
                  {g}
                </button>
              ))}
              {groups.length === 0 && <span className="font-mono text-xs text-zinc-500">Sin clases asignadas. Habla con la administración.</span>}
            </div>
            <input
              type="date"
              data-testid="teacher-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-lg border border-white/15 bg-panel px-4 py-2.5 font-mono text-sm text-white outline-none focus:border-accent"
            />
          </div>

          {sede && (
            <p className="mt-3 flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">
              <MapPin size={13} className="text-accent" /> {sede.name} · {sede.addr} · {groupMeta?.time}
            </p>
          )}

          <div className="relative mt-6 overflow-hidden rounded-xl border border-white/10 bg-card p-5 sm:p-7" data-testid="teacher-attendance-list">
            <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-accent/10 blur-[60px]" />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 font-display text-base font-extrabold uppercase tracking-tight">
                <ClipboardList size={17} className="text-accent" /> Asistencia · {group}
              </h2>
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                {counts.presente} presentes · {counts.falta} faltas · {counts.retraso} retrasos
              </span>
            </div>

            <div className="mt-5">
              <button
                data-testid="teacher-toggle-add-student"
                onClick={() => setShowAdd((v) => !v)}
                className="flex items-center gap-2 rounded-full border border-accent/60 bg-accent/10 px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-accent transition-all duration-300 hover:bg-accent hover:text-white"
              >
                <Plus size={14} /> Añadir alumna/o
              </button>
              {showAdd && (
                <form data-testid="teacher-add-student-form" onSubmit={addStudent} className="mt-3 grid grid-cols-1 gap-3 rounded-lg border border-accent/40 bg-panel p-4 sm:grid-cols-[1.2fr_1fr_0.8fr_auto]">
                  <input
                    required
                    data-testid="teacher-add-name"
                    value={addForm.name}
                    onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Nombre del alumno/a *"
                    className={inputCls}
                  />
                  <input
                    data-testid="teacher-add-guardian"
                    value={addForm.guardian_name}
                    onChange={(e) => setAddForm((f) => ({ ...f, guardian_name: e.target.value }))}
                    placeholder="Tutor/a (opcional)"
                    className={inputCls}
                  />
                  <input
                    data-testid="teacher-add-phone"
                    value={addForm.phone}
                    onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="Teléfono (opcional)"
                    className={inputCls}
                  />
                  <div className="flex gap-2">
                    <button type="submit" disabled={busy} className="flex-1 rounded-full bg-accent px-5 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-accent-hover disabled:opacity-60">
                      {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Añadir
                    </button>
                    <button type="button" onClick={() => setShowAdd(false)} className="rounded-full border border-white/15 px-4 font-mono text-[11px] uppercase text-zinc-400 transition-colors hover:text-white">
                      Cancelar
                    </button>
                  </div>
                </form>
              )}
            </div>

            {roster.length === 0 ? (
              <p data-testid="teacher-roster-empty" className="mt-6 rounded-lg border border-dashed border-white/15 p-6 text-center text-sm text-zinc-400">
                No hay alumnado matriculado en esta clase todavía. Si crees que faltan alumnos, avisa a la administración para cargar el listado.
              </p>
            ) : (
              <div className="mt-6 space-y-3">
                {roster.map((s) => (
                  <div key={s.email} data-testid={`attendance-row-${tid(s.email)}`} className="rounded-lg border border-white/10 bg-panel p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="font-bold text-white">{s.name}</p>
                        {s.guardian_name && <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-zinc-500">Tutor: {s.guardian_name} · {s.phone}</p>}
                      </div>
                      <div className="flex gap-2">
                        {STATUS_BTNS.map((b) => (
                          <button
                            key={b.id}
                            data-testid={`attendance-${b.id}-${tid(s.email)}`}
                            onClick={() => mark(s.email, b.id)}
                            className={`rounded-full border px-3.5 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.1em] transition-all duration-300 ${
                              marks[s.email]?.status === b.id ? b.on : `${b.off} bg-white/[0.03]`
                            }`}
                          >
                            {b.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <input
                      data-testid={`attendance-note-${tid(s.email)}`}
                      value={marks[s.email]?.note || ""}
                      onChange={(e) => note(s.email, e.target.value)}
                      placeholder="Nota opcional: ej. Llegó 15 min tarde, avisó por enfermedad…"
                      className="mt-3 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white placeholder-zinc-500 outline-none transition-colors focus:border-accent"
                    />
                  </div>
                ))}

                <button
                  data-testid="teacher-save-attendance"
                  onClick={save}
                  disabled={busy || !group}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-white shadow-[0_0_28px_rgba(255,0,122,0.4)] transition-all duration-300 hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {busy ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                  Guardar asistencia · {fmtDay(date)}
                </button>
              </div>
            )}
          </div>

          <div className="mt-6 overflow-hidden rounded-xl border border-white/10 bg-card p-5 sm:p-7" data-testid="teacher-history-list">
            <h2 className="flex items-center gap-2 font-display text-base font-extrabold uppercase tracking-tight">
              <History size={17} className="text-accent" /> Histórico de asistencia
            </h2>
            {history.length === 0 ? (
              <p className="mt-6 rounded-lg border border-dashed border-white/15 p-6 text-center text-sm text-zinc-400">
                Todavía no hay registros guardados para esta clase.
              </p>
            ) : (
              <div className="mt-6 space-y-3">
                {history.map((h) => {
                  const c = { presente: 0, falta: 0, retraso: 0 };
                  (h.records || []).forEach((r) => {
                    if (c[r.status] !== undefined) c[r.status] += 1;
                  });
                  return (
                    <details key={h.id} data-testid={`teacher-history-${h.date}`} className="group rounded-lg border border-white/10 bg-panel p-4">
                      <summary className="flex cursor-pointer flex-wrap items-center justify-between gap-2 font-mono text-xs uppercase tracking-[0.15em] text-zinc-300">
                        <span className="flex items-center gap-2">
                          <CalendarCheck size={14} className="text-accent" />
                          {fmtDay(h.date)}
                        </span>
                        <span className="text-zinc-500">
                          {c.presente}P · {c.falta}F · {c.retraso}R
                        </span>
                      </summary>
                      <div className="mt-3 space-y-1.5 border-t border-white/10 pt-3">
                        {(h.records || []).map((r) => (
                          <p key={r.email} className="text-xs text-zinc-300">
                            <span className={`font-bold ${r.status === "presente" ? "text-emerald-400" : r.status === "falta" ? "text-accent" : "text-amber-300"}`}>
                              {STATUS_ES[r.status]}
                            </span>{" "}
                            · {r.name}
                            {r.note && <span className="text-zinc-500"> — {r.note}</span>}
                          </p>
                        ))}
                        {h.teacher_name && <p className="pt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">Registrado por {h.teacher_name}</p>}
                      </div>
                    </details>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {tab === "avisos" && (
        <div className="mt-8 space-y-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
            <form data-testid="teacher-event-form" onSubmit={createEvent} className="space-y-4 rounded-xl border border-white/10 bg-card p-6 lg:col-span-2">
              <h2 className="flex items-center gap-2 font-display text-sm font-extrabold uppercase">
                <CalendarDays size={15} className="text-accent" /> Nuevo evento
              </h2>
              <div>
                <label htmlFor="tev-title" className={labelCls}>Título *</label>
                <input id="tev-title" data-testid="teacher-event-title" required value={evForm.title} onChange={(e) => setEvForm((f) => ({ ...f, title: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label htmlFor="tev-date" className={labelCls}>Fecha y hora *</label>
                <input id="tev-date" data-testid="teacher-event-date" type="datetime-local" required value={evForm.date} onChange={(e) => setEvForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label htmlFor="tev-desc" className={labelCls}>Descripción</label>
                <textarea id="tev-desc" data-testid="teacher-event-description" rows={2} value={evForm.description} onChange={(e) => setEvForm((f) => ({ ...f, description: e.target.value }))} className={`${inputCls} resize-none`} />
              </div>
              <div>
                <p className={labelCls}>Clases destinatarias</p>
                <div className="flex flex-wrap gap-2">
                  {groups.map((g) => (
                    <button type="button" key={g} data-testid={`teacher-event-group-${tid(g)}`} onClick={() => toggleEvGroup(g)} className={`rounded-full px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.1em] transition-all duration-300 ${evForm.groups.includes(g) ? "bg-accent text-white" : "border border-white/15 text-zinc-400"}`}>
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              <button type="submit" data-testid="teacher-event-submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-accent-hover disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Publicar evento
              </button>
            </form>
            <div className="space-y-2 rounded-xl border border-white/10 bg-card p-6 lg:col-span-3" data-testid="teacher-events-list">
              {tEvents.length === 0 && <p className="py-6 text-center text-sm text-zinc-400">Sin eventos todavía.</p>}
              {tEvents.map((ev) => (
                <div key={ev.id} className="flex items-start justify-between gap-3 rounded-lg border border-white/10 bg-panel p-4">
                  <div>
                    <p className="font-display text-sm font-extrabold uppercase text-white">{ev.title}</p>
                    <p className="mt-1 font-mono text-xs text-accent">{fmtEv(ev.date)} · {ev.groups.join(" · ")}</p>
                    {ev.description && <p className="mt-1 text-xs text-zinc-400">{ev.description}</p>}
                  </div>
                  <button data-testid={`teacher-event-delete-${ev.id}`} onClick={() => delEvent(ev.id)} aria-label="Eliminar" className="rounded-full border border-white/15 p-2 text-zinc-400 transition-colors hover:border-accent hover:text-accent">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
            <form data-testid="teacher-cancellation-form" onSubmit={createCancellation} className="space-y-4 rounded-xl border border-white/10 bg-card p-6 lg:col-span-2">
              <h2 className="flex items-center gap-2 font-display text-sm font-extrabold uppercase">
                <CalendarX2 size={15} className="text-accent" /> Cancelación de clase
              </h2>
              <div>
                <label htmlFor="tcx-group" className={labelCls}>Clase *</label>
                <select id="tcx-group" data-testid="teacher-cancellation-group" required value={cxF.group_code} onChange={(e) => setCxF((f) => ({ ...f, group_code: e.target.value }))} className={`${inputCls} appearance-none`}>
                  <option value="" disabled className="bg-panel">Selecciona tu clase</option>
                  {groups.map((g) => (
                    <option key={g} value={g} className="bg-panel">{g}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="tcx-date" className={labelCls}>Fecha y hora de la clase cancelada *</label>
                <input id="tcx-date" data-testid="teacher-cancellation-date" type="datetime-local" required value={cxF.date} onChange={(e) => setCxF((f) => ({ ...f, date: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label htmlFor="tcx-reason" className={labelCls}>Motivo</label>
                <textarea id="tcx-reason" data-testid="teacher-cancellation-reason" rows={2} value={cxF.reason} onChange={(e) => setCxF((f) => ({ ...f, reason: e.target.value }))} className={`${inputCls} resize-none`} />
              </div>
              <button type="submit" data-testid="teacher-cancellation-submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-accent-hover disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Publicar aviso
              </button>
            </form>
            <div className="space-y-2 rounded-xl border border-white/10 bg-card p-6 lg:col-span-3" data-testid="teacher-cancellations-list">
              {tCancellations.length === 0 && <p className="py-6 text-center text-sm text-zinc-400">Sin cancelaciones.</p>}
              {tCancellations.map((c) => (
                <div key={c.id} className="flex items-start justify-between gap-3 rounded-lg border border-accent/30 bg-panel p-4">
                  <div>
                    <p className="font-display text-sm font-extrabold uppercase text-white">Clase cancelada · {c.group_code}</p>
                    <p className="mt-1 font-mono text-xs text-accent">{fmtEv(c.date)}</p>
                    {c.reason && <p className="mt-1 text-xs text-zinc-400">{c.reason}</p>}
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{c.confirmed_count} confirmaciones</p>
                  </div>
                  <button data-testid={`teacher-cancellation-delete-${c.id}`} onClick={() => delCx(c.id)} aria-label="Eliminar" className="rounded-full border border-white/15 p-2 text-zinc-400 transition-colors hover:border-accent hover:text-accent">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
            <form data-testid="teacher-notice-form" onSubmit={createNotice} className="space-y-4 rounded-xl border border-white/10 bg-card p-6 lg:col-span-2">
              <h2 className="flex items-center gap-2 font-display text-sm font-extrabold uppercase">
                <Megaphone size={15} className="text-accent" /> Comunicado
              </h2>
              <div>
                <label htmlFor="tn-title" className={labelCls}>Título *</label>
                <input id="tn-title" data-testid="teacher-notice-title" required value={nf.title} onChange={(e) => setNf((f) => ({ ...f, title: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label htmlFor="tn-body" className={labelCls}>Mensaje</label>
                <textarea id="tn-body" data-testid="teacher-notice-body" rows={3} value={nf.body} onChange={(e) => setNf((f) => ({ ...f, body: e.target.value }))} className={`${inputCls} resize-none`} />
              </div>
              <div>
                <p className={labelCls}>Clases destinatarias</p>
                <div className="flex flex-wrap gap-2">
                  {groups.map((g) => (
                    <button type="button" key={g} data-testid={`teacher-notice-group-${tid(g)}`} onClick={() => toggleNfGroup(g)} className={`rounded-full px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.1em] transition-all duration-300 ${nf.groups.includes(g) ? "bg-accent text-white" : "border border-white/15 text-zinc-400"}`}>
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              <button type="submit" data-testid="teacher-notice-submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-accent-hover disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Publicar mensaje
              </button>
            </form>
            <div className="space-y-2 rounded-xl border border-white/10 bg-card p-6 lg:col-span-3" data-testid="teacher-notices-list">
              {tNotices.length === 0 && <p className="py-6 text-center text-sm text-zinc-400">Sin comunicados.</p>}
              {tNotices.map((n) => (
                <div key={n.id} className="flex items-start justify-between gap-3 rounded-lg border border-white/10 bg-panel p-4">
                  <div>
                    <p className="font-display text-sm font-extrabold uppercase text-white">{n.title}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{n.created_at?.slice(0, 10)} · {n.groups.join(" · ")}</p>
                    {n.body && <p className="mt-1 text-xs text-zinc-400">{n.body}</p>}
                  </div>
                  <button data-testid={`teacher-notice-delete-${n.id}`} onClick={() => delNotice(n.id)} aria-label="Eliminar" className="rounded-full border border-white/15 p-2 text-zinc-400 transition-colors hover:border-accent hover:text-accent">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
